import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Tag, Tour, Typography } from 'antd';
import type { TourProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GuideDefinition, GuideRole, GUIDE_CURRENT_UPDATE_ID, GUIDE_UPDATES, PLATFORM_GUIDES } from '../utils/platformGuides';
import { getGuideState, GuideState, markGuideCompleted, markGuideSeen, setGuideIntroStatus } from '../utils/platformGuideState';
import './PlatformGuide.css';

interface GuideContext { openGuide: () => void; unseenCount: number }
const Context = createContext<GuideContext>({ openGuide: () => undefined, unseenCount: 0 });
export const usePlatformGuide = (): GuideContext => useContext(Context);
const EMPTY: GuideState = { introStatus: 'pending', seenUpdateIds: [], completedGuideIds: [] };
const AUTO_ROUTES = new Set(['/', '/videos', '/admin/videos', '/admin/board', '/admin/questions', '/admin/tests']);
const GUIDE_ROUTES = new Set([...Array.from(AUTO_ROUTES), '/tests', '/results']);
interface RunningGuide { owner: string; id: string; current: number }

function visibleTarget(selector: string, folderScope?: string | null): HTMLElement | null {
  if (!selector) return null;
  try {
    const matches = Array.from(document.querySelectorAll<HTMLElement>(selector));
    const scoped = folderScope && matches.some(node => node.closest('[data-folder-id]'))
      ? matches.filter(node => node.closest('[data-folder-id]')?.getAttribute('data-folder-id') === folderScope || node.closest('.ant-modal'))
      : matches;
    return scoped.find(node => {
      if (!node.isConnected || node.closest('[hidden],[aria-hidden="true"],[inert]')) return false;
      let current: HTMLElement | null = node;
      while (current) {
        const style = window.getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;
        current = current.parentElement;
      }
      const rect = node.getBoundingClientRect();
      return node.getClientRects().length > 0 && rect.width > 0 && rect.height > 0;
    }) || null;
  } catch { return null; }
}

/** Passive guidance: all navigation and application actions remain explicit user choices. */
export const PlatformGuideProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const role: GuideRole = isAdmin ? 'admin' : 'employee';
  const owner = user ? `${user.id}:${role}` : '';
  const userId = user?.id;
  const ownerRef = useRef(owner); ownerRef.current = owner;
  const [saved, setSaved] = useState<{ owner: string; state: GuideState }>({ owner: '', state: EMPTY });
  const [centerOwner, setCenterOwner] = useState('');
  const [noticeOwner, setNoticeOwner] = useState('');
  const [section, setSection] = useState<'guides' | 'updates'>('guides');
  const [running, setRunning] = useState<RunningGuide | null>(null);
  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [restart, setRestart] = useState(0);
  const [search, setSearch] = useState('');
  const suppressed = useRef(false);
  const clickedStep = useRef('');
  const folderScope = useRef<string | null>(null);
  const pendingStart = useRef<{ owner: string; id: string; route: string } | null>(null);
  const originalFocus = useRef<HTMLElement | null>(null);
  const restoreAllowed = useRef(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const state = saved.owner === owner ? saved.state : EMPTY;
  const roleGuides = PLATFORM_GUIDES.filter(guide => guide.roles.includes(role));
  const matchesContext = (guide: GuideDefinition) => {
    if (!guide.contextOnly) return guide.route === location.pathname;
    try { return guide.matchPath ? new RegExp(guide.matchPath).test(location.pathname) : guide.route === location.pathname; }
    catch { return false; }
  };
  const guides = roleGuides.filter(guide => {
    if (!guide.contextOnly) return GUIDE_ROUTES.has(location.pathname);
    if (!matchesContext(guide)) return false;
    const node = guide.startTarget ? document.querySelector<HTMLElement>(guide.startTarget) : null;
    return !guide.startTarget || Boolean(node && !node.closest('[hidden]') && node.getClientRects().length);
  });
  const updates = GUIDE_UPDATES.filter(update => update.roles.includes(role));
  const unseen = updates.filter(update => !state.seenUpdateIds.includes(update.id));
  const centerOpen = Boolean(owner && centerOwner === owner);
  const noticeOpen = Boolean(owner && noticeOwner === owner);
  const active = running?.owner === owner ? roleGuides.find(guide => guide.id === running.id) : undefined;
  const current = active && running ? running.current : 0;
  const step = active?.steps[current];
  const guideOpen = Boolean(active);

  useEffect(() => {
    if (!guideOpen) return;
    document.documentElement.setAttribute('data-platform-guide-open', 'true');
    return () => document.documentElement.removeAttribute('data-platform-guide-open');
  }, [guideOpen]);

  const restoreFocus = useCallback(() => {
    if (!restoreAllowed.current) return;
    window.requestAnimationFrame(() => {
      if (!restoreAllowed.current) return;
      const remembered = originalFocus.current;
      const button = remembered?.isConnected && remembered.matches('button,[role="button"],a[href]')
        ? remembered : visibleTarget('[data-guide="help"]');
      button?.focus();
    });
  }, []);
  const refreshState = useCallback(() => {
    if (user && ownerRef.current === owner) setSaved({ owner, state: getGuideState(user.id, role) });
  }, [owner, role, user]);
  const acknowledgeUpdates = useCallback(() => {
    if (!user || ownerRef.current !== owner) return;
    GUIDE_UPDATES.filter(update => update.roles.includes(role)).forEach(update => markGuideSeen(user.id, role, update.id));
    refreshState();
  }, [owner, refreshState, role, user]);

  useEffect(() => {
    setSaved({ owner, state: userId ? getGuideState(userId, role) : EMPTY });
    setCenterOwner(''); setNoticeOwner(''); setRunning(null); setTarget(null);
    suppressed.current = false; clickedStep.current = ''; folderScope.current = null; pendingStart.current = null;
    originalFocus.current = null; restoreAllowed.current = false;
  }, [owner, role, userId]);
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!media) return;
    const change = () => setReducedMotion(media.matches);
    change(); media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  useEffect(() => {
    setRunning(null); setTarget(null); setNoticeOwner(''); setCenterOwner(''); clickedStep.current = ''; folderScope.current = null;
    const pending = pendingStart.current;
    pendingStart.current = null;
    if (pending && pending.owner === owner && pending.route === location.pathname) {
      setRunning({ owner, id: pending.id, current: 0 });
    }
  }, [location.pathname, location.search, owner]);

  const openGuide = useCallback(() => {
    if (!user) return;
    originalFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    restoreAllowed.current = false; suppressed.current = true;
    setRunning(null); folderScope.current = null; setNoticeOwner(''); setSearch(''); setSection('guides'); setCenterOwner(owner);
    refreshState();
  }, [owner, refreshState, user]);
  const closeCenter = () => {
    acknowledgeUpdates(); setCenterOwner(''); suppressed.current = true;
    restoreAllowed.current = true; restoreFocus();
  };
  const closeNotice = (defer: boolean) => {
    if (!user || ownerRef.current !== owner) return;
    suppressed.current = true; setNoticeOwner('');
    if (defer) {
      if (state.introStatus !== 'completed' && state.introStatus !== 'dismissed') setGuideIntroStatus(user.id, role, 'deferred');
    } else {
      acknowledgeUpdates();
      if (state.introStatus !== 'completed') setGuideIntroStatus(user.id, role, 'dismissed');
    }
    refreshState(); restoreAllowed.current = true; restoreFocus();
  };
  const closeTour = () => {
    setRunning(null); setTarget(null); clickedStep.current = ''; folderScope.current = null; suppressed.current = true;
    restoreAllowed.current = true; restoreFocus();
  };
  const beginGuide = (guide: GuideDefinition, allowNavigation = true) => {
    if (!user || !guide.roles.includes(role)) return;
    if (guide.contextOnly ? !matchesContext(guide) : !GUIDE_ROUTES.has(guide.route) || !GUIDE_ROUTES.has(location.pathname)) return;
    if (!allowNavigation && !matchesContext(guide)) { setCenterOwner(owner); return; }
    acknowledgeUpdates(); suppressed.current = true; restoreAllowed.current = false;
    setCenterOwner(''); setNoticeOwner(''); setTarget(null); clickedStep.current = ''; folderScope.current = null;
    if (!guide.contextOnly && guide.route !== location.pathname) {
      pendingStart.current = { owner, id: guide.id, route: guide.route };
      setRunning(null); navigate(guide.route);
    } else setRunning({ owner, id: guide.id, current: 0 });
  };
  const finishGuide = () => {
    if (!user || !active || ownerRef.current !== owner) return;
    markGuideCompleted(user.id, role, active.id); setGuideIntroStatus(user.id, role, 'completed');
    refreshState(); closeTour();
  };
  const changeStep = (next: number) => {
    if (!active || !running || next < 0 || next >= active.steps.length) return;
    if (next > current && target && step?.advanceWhen) return;
    clickedStep.current = ''; setTarget(null); setRunning({ ...running, current: next });
  };

  useEffect(() => {
    if (loading || !user || !isAdmin || saved.owner !== owner || !AUTO_ROUTES.has(location.pathname) || suppressed.current || active || centerOpen || noticeOpen) return;
    if ((state.introStatus === 'completed' || state.introStatus === 'dismissed') && unseen.length === 0) return;
    let timer: number | undefined;
    const consider = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (suppressed.current || ownerRef.current !== owner) return;
        if (visibleTarget('.ant-modal') || visibleTarget('.ant-drawer') || visibleTarget('.ant-dropdown:not(.ant-dropdown-hidden)')) return;
        if (document.activeElement instanceof HTMLElement && document.activeElement.matches('input,textarea,select,[contenteditable="true"]')) return;
        suppressed.current = true; restoreAllowed.current = false; setNoticeOwner(owner);
      }, 500);
    };
    consider();
    const observer = new MutationObserver(consider);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class', 'style'] });
    document.addEventListener('focusout', consider);
    return () => { if (timer !== undefined) window.clearTimeout(timer); observer.disconnect(); document.removeEventListener('focusout', consider); };
  }, [loading, user, isAdmin, saved.owner, owner, location.pathname, active, centerOpen, noticeOpen, state.introStatus, unseen.length]);

  useEffect(() => {
    if (!active || !step) { setTarget(null); return; }
    const stepKey = `${owner}:${active.id}:${current}`;
    clickedStep.current = '';
    let frame: number | undefined;
    let alignmentTimer: number | undefined;
    let previousRect = '';
    let preparedNode: HTMLElement | null = null;
    let disposed = false;
    const updateTarget = () => {
      if (disposed) return;
      let node = visibleTarget(step.target, folderScope.current);
      if (!folderScope.current && node?.closest('[data-folder-id]')) {
        folderScope.current = node.closest('[data-folder-id]')!.getAttribute('data-folder-id');
        node = visibleTarget(step.target, folderScope.current);
      }
      if (node) {
        const header = document.querySelector<HTMLElement>('.app-header');
        const inset = node.closest('.ant-modal') ? 12 : Math.max(12, (header?.getBoundingClientRect().bottom || 0) + 12);
        const rect = node.getBoundingClientRect();
        if ((node !== preparedNode && (rect.top < inset || rect.bottom > window.innerHeight - 12)) || rect.top < inset) {
          node.scrollIntoView?.({ behavior: 'auto', block: rect.height > window.innerHeight - inset - 12 ? 'start' : 'center', inline: 'nearest' });
        }
        preparedNode = node;
      }
      setTarget(previous => previous === node ? previous : node);
      // This installed Tour listens to resize, but not scroll or modal zoom.
      // Reuse that measurement path after settling, without remounting its
      // portal (which would restart auto-scroll and the body scroll lock).
      if (node) {
        const rect = node.getBoundingClientRect();
        const signature = [rect.left, rect.top, rect.width, rect.height].map(Math.round).join(':');
        if (!previousRect) previousRect = signature;
        else if (signature !== previousRect) {
          if (alignmentTimer !== undefined) window.clearTimeout(alignmentTimer);
          alignmentTimer = window.setTimeout(() => {
            if (disposed) return;
            const settled = visibleTarget(step.target, folderScope.current)?.getBoundingClientRect();
            if (!settled) return;
            const next = [settled.left, settled.top, settled.width, settled.height].map(Math.round).join(':');
            if (next !== previousRect) {
              previousRect = next;
              window.dispatchEvent(new Event('resize'));
            }
          }, 80);
        }
      }
      if (step.advanceWhen && clickedStep.current === stepKey && visibleTarget(step.advanceWhen, folderScope.current)) {
        clickedStep.current = '';
        setTarget(null);
        setRunning(previous => previous && previous.owner === owner && previous.id === active.id && previous.current === current
          ? { ...previous, current: Math.min(current + 1, active.steps.length - 1) } : previous);
      }
    };
    const schedule = () => { if (frame === undefined) frame = window.requestAnimationFrame(() => { frame = undefined; updateTarget(); }); };
    const clicked = (event: MouseEvent) => {
      if (!event.isTrusted || !step.advanceWhen) return;
      const node = visibleTarget(step.target, folderScope.current);
      if (node && event.target instanceof Node && node.contains(event.target)) { clickedStep.current = stepKey; schedule(); }
    };
    updateTarget();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class', 'style'] });
    document.addEventListener('click', clicked, true); document.addEventListener('scroll', schedule, true); document.addEventListener('animationend', schedule, true); window.addEventListener('resize', schedule);
    return () => { disposed = true; observer.disconnect(); document.removeEventListener('click', clicked, true); document.removeEventListener('scroll', schedule, true); document.removeEventListener('animationend', schedule, true); window.removeEventListener('resize', schedule); if (frame !== undefined) window.cancelAnimationFrame(frame); if (alignmentTimer !== undefined) window.clearTimeout(alignmentTimer); };
  }, [active, current, owner, step, running?.id, restart]);

  useEffect(() => {
    if (!active && !centerOpen && !noticeOpen) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (active) closeTour(); else if (centerOpen) closeCenter(); else closeNotice(false);
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  });

  const context = useMemo(() => ({ openGuide, unseenCount: user ? unseen.length : 0 }), [openGuide, unseen.length, user]);
  const tourSteps: TourProps['steps'] = active?.steps.map((item, index) => ({
    title: item.title, target: index === current ? target : null,
    scrollIntoViewOptions: { behavior: 'auto', block: target && target.getBoundingClientRect().height > window.innerHeight - 112 ? 'start' : 'center', inline: 'nearest' },
    description: <div className="platform-guide-step-description"><Typography.Paragraph>{item.description}</Typography.Paragraph>
      {index === current && !target && <Alert type="info" showIcon message="Такого блока сейчас нет на экране. Пропустите шаг или вернитесь к нему, когда появятся нужные материалы." />}
      {index === current && target && item.advanceWhen && <Typography.Paragraph className="platform-guide-wait">Нажмите выделенную кнопку самостоятельно. Следующий шаг появится после открытия формы.</Typography.Paragraph>}
    </div>,
  }));
  const renderUpdates = (items = updates) => <div className="platform-guide-updates">{items.map(update => <article key={update.id} className="platform-guide-update">
    <Typography.Title level={4}>{update.title}</Typography.Title><Typography.Paragraph>{update.description}</Typography.Paragraph>
    {GUIDE_ROUTES.has(location.pathname) && <Space wrap className="platform-guide-update-links">{update.guideIds.map(id => {
      const guide = roleGuides.find(item => item.id === id && !item.contextOnly);
      return guide ? <Button key={id} size="small" onClick={() => beginGuide(guide)}>{guide.title}</Button> : null;
    })}</Space>}
  </article>)}{items.length === 0 && <Typography.Paragraph>Все обновления просмотрены.</Typography.Paragraph>}</div>;
  const currentGuides = guides.filter(matchesContext);
  const orderedGuides = [...currentGuides, ...guides.filter(guide => !matchesContext(guide))].filter(guide => `${guide.title} ${guide.description}`.toLocaleLowerCase('ru').includes(search.trim().toLocaleLowerCase('ru')));

  return <Context.Provider value={context}>{children}
    <Modal className="platform-guide-center" title="Помощь по платформе" width={800} zIndex={2400} open={centerOpen}
      onCancel={closeCenter} footer={<Button onClick={closeCenter}>Закрыть</Button>} afterClose={restoreFocus} focusTriggerAfterClose={false}>
      <div className="platform-guide-section-switch" role="group" aria-label="Раздел помощи">
        <Button type={section === 'guides' ? 'primary' : 'default'} aria-pressed={section === 'guides'} onClick={() => setSection('guides')}>Инструкции</Button>
        <Button type={section === 'updates' ? 'primary' : 'default'} aria-pressed={section === 'updates'} onClick={() => setSection('updates')}>Что нового{unseen.length > 0 ? ` · ${unseen.length}` : ''}</Button>
      </div>
      {section === 'guides' && <Input className="platform-guide-search" aria-label="Найти инструкцию" placeholder="Найти инструкцию" allowClear value={search} onChange={event => setSearch(event.target.value)} />}
      {section === 'updates' ? renderUpdates() : <div className="platform-guide-list">{orderedGuides.length === 0 && <Typography.Paragraph>{search ? 'По запросу ничего не найдено.' : 'Для этого экрана пока нет отдельного обзора. Откройте помощь из списка тестов, материалов или личного кабинета после завершения работы.'}</Typography.Paragraph>}{orderedGuides.map(guide => <article key={guide.id} className="platform-guide-card">
        <div><Typography.Title level={4}>{guide.title}</Typography.Title>
          <Space wrap>{matchesContext(guide) && <Tag color="blue">Текущий раздел</Tag>}{state.completedGuideIds.includes(guide.id) && <Tag color="success">Обзор пройден</Tag>}</Space>
          <Typography.Paragraph>{guide.description}</Typography.Paragraph></div>
        <Button onClick={() => beginGuide(guide)}>{state.completedGuideIds.includes(guide.id) ? 'Повторить обзор' : 'Начать обзор'}</Button>
      </article>)}</div>}
    </Modal>
    <Modal className="platform-guide-welcome" title={state.introStatus === 'completed' || state.introStatus === 'dismissed' ? 'Что нового на платформе' : 'Добро пожаловать в FEBEST'}
      width={640} zIndex={2400} open={noticeOpen} onCancel={() => closeNotice(false)} afterClose={restoreFocus} focusTriggerAfterClose={false}
      footer={<Space><Button onClick={() => closeNotice(true)}>Позже</Button><Button type="primary" onClick={() => {
        const guide = currentGuides[0];
        if (guide) beginGuide(guide, false); else { acknowledgeUpdates(); setNoticeOwner(''); setCenterOwner(owner); }
      }}>Начать обзор</Button></Space>}>
      <Typography.Paragraph>Короткие инструкции помогут освоиться. Вы сможете повторить любой обзор через кнопку «Помощь» вверху страницы.</Typography.Paragraph>
      {renderUpdates(unseen)}
    </Modal>
    <Tour rootClassName="platform-guide-tour" open={Boolean(active)} current={current} steps={tourSteps} onChange={changeStep}
      onClose={closeTour} onFinish={finishGuide} zIndex={2500} disabledInteraction={false}
      animated={!reducedMotion} scrollIntoViewOptions={{ behavior: 'auto', block: 'center' }}
      indicatorsRender={() => active ? <span className="platform-guide-indicator">Шаг {current + 1} из {active.steps.length}</span> : null}
      actionsRender={() => <div className="platform-guide-tour-actions"><Button onClick={() => { clickedStep.current = ''; folderScope.current = null; setTarget(null); setRestart(value => value + 1); if (running) setRunning({ ...running, current: 0 }); }}>Сначала</Button>
        <Button onClick={closeTour}>Выйти</Button><Button disabled={current === 0} onClick={() => changeStep(current - 1)}>Назад</Button>
          <Button type="primary" disabled={Boolean(target && step?.advanceWhen)} onClick={() => active && current === active.steps.length - 1 ? finishGuide() : changeStep(current + 1)}>
            {active && current === active.steps.length - 1 ? 'Завершить обзор' : !target ? 'Пропустить шаг' : 'Далее'}</Button></div>} />
    <span hidden data-platform-guide-version={GUIDE_CURRENT_UPDATE_ID} />
  </Context.Provider>;
};

export default PlatformGuideProvider;
